import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft, ShieldCheck, Zap, MessageCircle, ChevronDown } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useContent, API } from "@/context/ContentContext";

const DEVICES = [
  "Smart TV",
  "Android Phone / Tablet",
  "iPhone / iPad",
  "Android TV / Box",
  "Fire Stick / Streaming Device",
  "PC / Laptop",
];

const inputCls =
  "w-full rounded-xl border border-slate-900/10 bg-surface px-4 py-3.5 text-sm text-slate-800 placeholder:text-slate-500 transition-colors focus:border-electric/60 focus:outline-none focus:ring-1 focus:ring-electric/40";

export default function Order() {
  const { content, reload } = useContent();
  const [searchParams] = useSearchParams();
  const [planId, setPlanId] = useState(searchParams.get("plan") || "");
  const [form, setForm] = useState({ name: "", whatsapp: "", email: "", device: "", payment_method: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    document.title = "Order — IPTV HUB";
  }, []);

  if (!content) return <main className="min-h-screen pt-[72px]" />;
  const plans = content.plans;
  const plan = plans.find((p) => p.id === planId) || plans.find((p) => p.id === searchParams.get("plan")) || plans[0];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.whatsapp.trim() || !form.email.trim() || !form.device || !form.payment_method) {
      toast.error("Please complete every field to place your order.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        plan_id: plan.id,
        plan_name: plan.name,
        price: plan.price,
        ...form,
      });
      setConfirmed(data);
      reload();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Could not place your order. Please try again or contact us on WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <main className="hero-bg flex min-h-screen items-center pt-[72px]">
        <div className="mx-auto w-full max-w-2xl px-4 py-20 sm:px-6">
          <Reveal>
            <div className="glass rounded-3xl p-10 text-center" data-testid="order-confirmation-card">
              <CheckCircle2 className="mx-auto h-16 w-16 text-electric drop-shadow-[0_0_16px_rgba(0,240,255,0.6)]" />
              <h1 className="mt-6 font-display text-3xl font-extrabold text-slate-900">Order received!</h1>
              <p className="mt-3 text-sm leading-relaxed text-slate-500">{content.settings.order_success_note}</p>
              <div className="mt-6 inline-flex items-center gap-3 rounded-xl border border-electric/30 bg-electric/5 px-5 py-3">
                <span className="text-xs text-slate-500">Order ID</span>
                <span className="font-mono text-lg font-bold tracking-widest text-electric" data-testid="order-confirmation-id">
                  {confirmed.order_id}
                </span>
              </div>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
                {confirmed.whatsapp_url && (
                  <a
                    href={confirmed.whatsapp_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="order-whatsapp-send-button"
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#25D366] px-6 py-4 text-sm font-bold text-slate-900 shadow-[0_0_28px_rgba(37,211,102,0.4)] transition-transform hover:scale-[1.02]"
                  >
                    <MessageCircle className="h-5 w-5" /> Send Order on WhatsApp
                  </a>
                )}
                <Link to="/" data-testid="order-back-home-button" className="btn-ghost inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-4 text-sm">
                  <ArrowLeft className="h-4 w-4" /> Back to Home
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </main>
    );
  }

  return (
    <main className="hero-bg min-h-screen pt-[72px] pb-24">
      <div className="mx-auto max-w-6xl px-4 pt-12 sm:px-6 lg:px-8">
        <Reveal>
          <div className="mb-10 flex items-center gap-4">
            <Link to="/" data-testid="order-back-link" className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-900/10 bg-slate-900/[0.04] text-slate-600 transition-colors hover:text-electric">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-electric">Order</p>
              <h1 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Complete your order</h1>
            </div>
          </div>
        </Reveal>

        <div className="grid gap-8 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            <form onSubmit={submit} className="glass rounded-3xl p-8" data-testid="order-form">
              <label className="block">
                <span className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Selected plan</span>
                <div className="relative">
                  <select
                    data-testid="order-plan-select"
                    value={plan?.id}
                    onChange={(e) => setPlanId(e.target.value)}
                    className={`${inputCls} appearance-none pr-10`}
                  >
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} — ${Number(p.price).toFixed(2)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                </div>
              </label>

              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Customer name</span>
                  <input data-testid="order-name-input" className={inputCls} placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </label>
                <label className="block">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">WhatsApp number</span>
                  <input data-testid="order-whatsapp-input" className={inputCls} placeholder="e.g. 0341 1314403" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
                </label>
              </div>

              <label className="mt-5 block">
                <span className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Email</span>
                <input data-testid="order-email-input" type="email" className={inputCls} placeholder="you@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </label>

              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Device type</span>
                  <div className="relative">
                    <select data-testid="order-device-select" value={form.device} onChange={(e) => setForm({ ...form, device: e.target.value })} className={`${inputCls} appearance-none pr-10`}>
                      <option value="">Select device...</option>
                      {DEVICES.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  </div>
                </label>
                <label className="block">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Payment method</span>
                  <div className="relative">
                    <select data-testid="order-payment-select" value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className={`${inputCls} appearance-none pr-10`}>
                      <option value="">Select method...</option>
                      {(content.settings.payment_methods || []).map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  </div>
                </label>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="order-submit-button"
                className="btn-primary mt-8 w-full rounded-2xl px-6 py-4 text-base disabled:opacity-60"
              >
                {submitting ? "Placing order..." : `Place Order — $${Number(plan?.price || 0).toFixed(2)}`}
              </button>
              <p className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500">
                <ShieldCheck className="h-3.5 w-3.5 text-electric" /> We never ask for card details or sensitive information on this form.
              </p>
            </form>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-2">
            <div className="glass sticky top-24 rounded-3xl p-8" data-testid="order-summary-card">
              <h2 className="font-display text-lg font-bold text-slate-900">Order summary</h2>
              <div className="mt-5 flex items-center justify-between rounded-2xl border border-electric/25 bg-electric/5 px-5 py-4">
                <span className="font-display text-base font-bold text-slate-900" data-testid="order-summary-plan">{plan?.name}</span>
                <span className="font-mono text-lg font-bold text-electric" data-testid="order-summary-price">${Number(plan?.price || 0).toFixed(2)}</span>
              </div>
              <ul className="mt-6 space-y-3" data-testid="order-summary-features">
                {(plan?.features || []).map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-electric" /> {f}
                  </li>
                ))}
              </ul>
              <div className="mt-8 space-y-4 border-t border-slate-900/10 pt-6">
                {[
                  { icon: Zap, text: "Setup instructions sent within minutes" },
                  { icon: MessageCircle, text: "Payment details confirmed on WhatsApp" },
                  { icon: ShieldCheck, text: "Only the details we need — nothing more" },
                ].map((r, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs text-slate-500">
                    <r.icon className="h-4 w-4 shrink-0 text-electric" /> {r.text}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
